/**
 * Select presentation-only source context shown beside connector records.
 *
 * cm-coding: Workspace administrators may choose which provider context fields
 * reviewers see, but they may not hide identity, classification, or revision
 * data required for synchronization. This control edits only ordered display
 * keys so a presentation change cannot alter imported records or analysis.
 */
import { cn } from '@/lib/utils'
import type { ConnectorSourceDisplayFieldDefinition } from '../types'

export function orderedSourceDisplayFields(
  fields: ConnectorSourceDisplayFieldDefinition[],
  selectedKeys: string[],
): ConnectorSourceDisplayFieldDefinition[] {
  const fieldsByKey = new Map(fields.map(field => [field.key, field]))
  return selectedKeys.flatMap(key => {
    const field = fieldsByKey.get(key)
    return field ? [field] : []
  })
}

export function ConnectorSourceDisplayFieldSelection({
  fields,
  selectedKeys,
  onSelectedKeysChange,
  disabled = false,
}: {
  fields: ConnectorSourceDisplayFieldDefinition[]
  selectedKeys: string[]
  onSelectedKeysChange: (keys: string[]) => void
  disabled?: boolean
}) {
  const selectedFields = orderedSourceDisplayFields(fields, selectedKeys)
  const toggle = (key: string, selected: boolean) => {
    onSelectedKeysChange(selected
      ? selectedKeys.filter(candidate => candidate !== key)
      : [...selectedKeys, key])
  }

  return (
    <fieldset
      className="space-y-2"
      data-testid="connector-source-display-field-selection"
      disabled={disabled}
    >
      <div className="flex flex-wrap gap-1.5">
        {fields.map(field => {
          const selectedIndex = selectedKeys.indexOf(field.key)
          const selected = selectedIndex >= 0
          return (
            <button
              type="button"
              key={field.key}
              data-testid={`connector-source-display-field-${field.key}`}
              aria-label={field.label}
              aria-pressed={selected}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-lg border px-[11px] text-[11.5px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 motion-reduce:transition-none',
                disabled && 'cursor-not-allowed opacity-60',
                selected
                  ? 'border-[#1a2436] bg-[#1a2436] text-white hover:bg-[#2c3a52]'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
              )}
              onClick={() => toggle(field.key, selected)}
            >
              {selected && (
                <span
                  aria-hidden="true"
                  className="flex size-[15px] shrink-0 items-center justify-center rounded-full bg-white/20 text-[9.5px] font-bold text-white"
                >
                  {selectedIndex + 1}
                </span>
              )}
              {field.label}
            </button>
          )
        })}
      </div>
      <p className="text-[11.5px] text-slate-400">
        Selection order sets the display order on dashboard rows and CSV exports — it does not affect sync or documents.
      </p>
      <div className="flex min-w-0 items-baseline gap-[7px] rounded-lg border border-slate-100 bg-slate-50 px-[11px] py-2">
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400">
          Preview
        </span>
        <span className="min-w-0 truncate text-[11.5px] text-slate-600">
          {selectedFields.length
            ? selectedFields.map(field => field.label).join(' · ')
            : 'No dashboard fields selected'}
        </span>
      </div>
    </fieldset>
  )
}
